import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Calendar, TrendingUp, CheckCircle2,
  ClipboardList, Edit, Save, Waves, Smile, Download, Activity,
  Sparkles, Loader2, Star, Target, AlertTriangle, Brain, Minus, TrendingDown
} from 'lucide-react';

const COMFORT_LABELS = {
  very_happy: { label: 'Loves it!', emoji: '\u{1F604}', color: 'text-green-600 bg-green-50' },
  happy: { label: 'Happy', emoji: '\u{1F642}', color: 'text-emerald-600 bg-emerald-50' },
  neutral: { label: 'Okay', emoji: '\u{1F610}', color: 'text-yellow-600 bg-yellow-50' },
  uncomfortable: { label: 'Uneasy', emoji: '\u{1F61F}', color: 'text-orange-600 bg-orange-50' },
  upset: { label: 'Upset', emoji: '\u{1F622}', color: 'text-red-600 bg-red-50' },
};

const AI_TREND_ICON = { improving: TrendingUp, plateau: Minus, regression: TrendingDown, new: Star };
const AI_TREND_COLOR = { improving: 'text-green-600 bg-green-50', plateau: 'text-amber-600 bg-amber-50', regression: 'text-red-600 bg-red-50', new: 'text-blue-600 bg-blue-50' };

export default function MonthlyReportsPage() {
  const { childId } = useParams();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [generatingMissing, setGeneratingMissing] = useState(false);
  const [activeTab, setActiveTab] = useState('monthly');
  const [aiReports, setAiReports] = useState([]);
  const [activeAiReport, setActiveAiReport] = useState(null);
  const [showAiDetail, setShowAiDetail] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const isInstructor = user?.role === 'instructor';

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get(`/monthly-reports?child_id=${childId}`);
      setReports(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, [childId]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get(`/sessions?child_id=${childId}`);
      setSessions(res.data.filter(s => s.status === 'completed'));
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, [childId]);

  const fetchAiReports = useCallback(async () => {
    try {
      const res = await api.get(`/children/${childId}/ai-progress-reports`);
      setAiReports(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); }
  }, [childId]);

  useEffect(() => {
    fetchReports();
    fetchSessions();
    fetchAiReports();
  }, [fetchReports, fetchSessions, fetchAiReports]);

  const sortedSessions = useMemo(() =>
    [...sessions].sort((a, b) => b.session_number - a.session_number),
    [sessions]
  );

  const generateAiReport = async () => {
    setGeneratingAi(true);
    try {
      const res = await api.get(`/children/${childId}/ai-progress-report`);
      setAiReports(prev => [res.data, ...prev]);
      setActiveAiReport(res.data);
      setShowAiDetail(true);
      toast.success('AI progress report generated!');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) toast.error(detail || 'AI budget exceeded.');
      else if (status === 403) toast.error('Only instructors can generate AI reports');
      else if (status === 504) toast.error('AI is taking too long. Please try again in a moment.');
      else toast.error(detail || 'Failed to generate AI report');
    }
    setGeneratingAi(false);
  };

  const openReport = (report) => {
    setSelectedReport(report);
    setEditNotes(report.instructor_notes || '');
    setEditing(false);
    setShowDetail(true);
  };

  const saveNotes = async () => {
    if (!selectedReport) return;
    try {
      const res = await api.put(`/monthly-reports/${selectedReport.report_id}`, {
        instructor_notes: editNotes
      });
      setSelectedReport(res.data);
      setEditing(false);
      toast.success('Notes saved!');
      fetchReports();
    } catch { toast.error('Failed to save notes'); }
  };

  const publishReport = async () => {
    if (!selectedReport) return;
    try {
      const res = await api.put(`/monthly-reports/${selectedReport.report_id}`, {
        status: 'published'
      });
      setSelectedReport(res.data);
      toast.success('Report published!');
      fetchReports();
    } catch { toast.error('Failed to publish'); }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/monthly-reports/generate', { child_id: childId });
      if (res.data.report_id) {
        toast.success('Report generated!');
        fetchReports();
      } else {
        toast.info(res.data.message || 'No data available for report');
      }
    } catch { toast.error('Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const generateMissingReports = async () => {
    setGeneratingMissing(true);
    try {
      const res = await api.post('/monthly-reports/generate-missing', { child_id: childId });
      if (res.data.generated > 0) {
        toast.success(`Generated ${res.data.generated} missing report(s): ${res.data.reports.join(', ')}`);
        fetchReports();
      } else {
        toast.info('No missing reports to generate');
      }
    } catch { toast.error('Failed to generate missing reports'); }
    finally { setGeneratingMissing(false); }
  };

  const downloadPdf = async () => {
    if (!selectedReport) return;
    setDownloading(true);
    try {
      const res = await api.get(`/monthly-reports/${selectedReport.report_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SplashAble_${selectedReport.child_name}_${selectedReport.month_label}.pdf`.replace(/ /g, '_');
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  const downloadSessionPdf = async (session) => {
    try {
      const res = await api.get(`/sessions/${session.session_id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `SplashAble_Session${session.session_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Session PDF downloaded!');
    } catch { toast.error('Failed to download session PDF'); }
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Waves className="w-10 h-10 text-[#22D3EE] animate-pulse" /></div>;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="monthly-reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
            Reports
          </h1>
          <p className="text-slate-500 mt-1">Monthly summaries & individual session reports</p>
        </div>
        {isInstructor && activeTab === 'monthly' && (
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="generate-missing-reports-btn"
              onClick={generateMissingReports}
              disabled={generatingMissing}
              variant="outline"
              className="rounded-full border-[#22D3EE] text-[#0E7490] hover:bg-[#ECFEFF] font-bold gap-2"
            >
              <Calendar className="w-4 h-4" /> {generatingMissing ? 'Generating...' : 'Generate Past Reports'}
            </Button>
            <Button
              data-testid="generate-report-btn"
              onClick={generateReport}
              disabled={generating}
              className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
            >
              <FileText className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate This Month'}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="reports-tabs">
        <button
          data-testid="tab-monthly"
          onClick={() => setActiveTab('monthly')}
          className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
            activeTab === 'monthly'
              ? 'bg-[#1E1B4B] text-white shadow-lg'
              : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Monthly Reports</span>
        </button>
        <button
          data-testid="tab-sessions"
          onClick={() => setActiveTab('sessions')}
          className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
            activeTab === 'sessions'
              ? 'bg-[#1E1B4B] text-white shadow-lg'
              : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2"><Activity className="w-4 h-4" /> Session Reports ({sessions.length})</span>
        </button>
        <button
          data-testid="tab-ai-progress"
          onClick={() => setActiveTab('ai')}
          className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
            activeTab === 'ai'
              ? 'bg-[#1E1B4B] text-white shadow-lg'
              : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
          }`}
        >
          <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Progress ({aiReports.length})</span>
        </button>
      </div>

      {/* Monthly Reports Tab */}
      {activeTab === 'monthly' && (
        <>
          {reports.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Monthly Reports Yet</p>
                <p className="text-slate-400 mt-2 text-sm">Reports are generated monthly to track progress</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {reports.map(report => (
                <Card
                  key={report.report_id}
                  className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openReport(report)}
                  data-testid={`report-card-${report.report_id}`}
                >
                  <CardContent className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E1B4B] to-[#2D2B66] flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-[#C4F017]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{report.month_label}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span>{report.completed_sessions}/{report.total_sessions} sessions</span>
                          <span>{report.skills_mastered} skills</span>
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={`rounded-full ${report.status === 'published' ? 'border-green-200 text-green-600 bg-green-50' : 'border-amber-200 text-amber-600 bg-amber-50'}`}>
                      {report.status}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Session Reports Tab */}
      {activeTab === 'sessions' && (
        <>
          {sessions.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Activity className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Completed Sessions</p>
                <p className="text-slate-400 mt-2 text-sm">Session reports appear here after sessions are completed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {sortedSessions.map(session => {
                const skillsDone = (session.skills || []).filter(s => s.completed).length;
                const totalSkills = (session.skills || []).length;
                const dateStr = session.local_date || (session.date ? session.date.split('T')[0] : '');

                return (
                  <Card
                    key={session.session_id}
                    className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`session-report-${session.session_id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0E7490] to-[#22D3EE] flex items-center justify-center">
                            <span className="text-white font-extrabold text-lg" style={{ fontFamily: 'Nunito' }}>
                              {session.session_number}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>
                              Session #{session.session_number}
                            </h3>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              {dateStr && <span>{new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                              <span>{skillsDone}/{totalSkills} skills</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          data-testid={`download-session-pdf-${session.session_id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => downloadSessionPdf(session)}
                          className="rounded-full border-[#22D3EE] text-[#0E7490] hover:bg-[#ECFEFF] font-bold gap-2"
                        >
                          <Download className="w-4 h-4" /> PDF
                        </Button>
                      </div>
                      {session.instructor_notes && (
                        <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-xl p-3 line-clamp-2">
                          {session.instructor_notes}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* AI Progress Tab */}
      {activeTab === 'ai' && (
        <>
          {/* AI Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="ai-reports-disclaimer">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-800">AI-Generated Reports - For Reference Only</p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  These reports are generated by artificial intelligence. Always rely on professional judgment and direct observations.
                </p>
              </div>
            </div>
          </div>

          {isInstructor && (
            <div className="flex justify-end mb-4">
              <Button
                data-testid="generate-ai-report-btn"
                onClick={generateAiReport}
                disabled={generatingAi}
                className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
              >
                {generatingAi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generatingAi ? 'Generating...' : 'Generate AI Report'}
              </Button>
            </div>
          )}

          {aiReports.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Brain className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No AI Progress Reports Yet</p>
                <p className="text-slate-400 mt-2 text-sm text-center max-w-md">
                  {isInstructor
                    ? 'Generate an AI progress report to see analysis of this child\'s swim journey.'
                    : 'AI progress reports will appear here once your instructor generates them.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {aiReports.map(report => (
                <Card
                  key={report.report_id}
                  className="rounded-2xl border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { setActiveAiReport(report); setShowAiDetail(true); }}
                  data-testid={`ai-report-card-${report.report_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#312E81] to-[#4338CA] flex items-center justify-center">
                          <Sparkles className="w-6 h-6 text-[#C4F017]" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>
                            {report.month || new Date(report.generated_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </h3>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                            {report.overall_progress?.slice(0, 80)}...
                          </p>
                        </div>
                      </div>
                      <Badge className="rounded-full bg-purple-50 text-purple-600 border-purple-200">AI Report</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* AI Progress Report Detail Dialog */}
      <Dialog open={showAiDetail} onOpenChange={setShowAiDetail}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-auto">
          {activeAiReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                  <Sparkles className="w-5 h-5 text-[#C4F017]" />
                  AI Progress Report — {activeAiReport.month || 'Monthly'}
                </DialogTitle>
              </DialogHeader>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">AI-Generated - For Reference Only. Use professional judgment.</p>
                </div>
              </div>

              <div className="space-y-4 pt-2">
                {/* Overall Progress */}
                <div className="bg-gradient-to-br from-[#1E1B4B] to-[#312E81] text-white rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="w-4 h-4 text-[#22D3EE]" />
                    <h3 className="font-bold text-sm">Progress Summary</h3>
                  </div>
                  <p className="text-sm text-slate-200 leading-relaxed">{activeAiReport.overall_progress}</p>
                  <p className="text-xs text-slate-400 mt-3">Generated {new Date(activeAiReport.generated_at).toLocaleString()}</p>
                </div>

                {/* Skill Progression */}
                {activeAiReport.skill_progression?.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-700 text-sm mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#0E7490]" /> Skill Progression
                    </h3>
                    <div className="space-y-2">
                      {activeAiReport.skill_progression.map((sp, i) => {
                        const TIcon = AI_TREND_ICON[sp.trend] || Minus;
                        const colors = AI_TREND_COLOR[sp.trend] || 'text-slate-600 bg-slate-50';
                        return (
                          <div key={`mr-${i}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${colors}`}>
                              <TIcon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className="text-sm font-semibold text-slate-800">{sp.skill}</p>
                                <Badge className={`rounded-full text-[10px] capitalize ${colors}`}>{sp.trend}</Badge>
                              </div>
                              <p className="text-xs text-slate-500">{sp.detail}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {activeAiReport.strengths_observed?.length > 0 && (
                    <div className="bg-green-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> Strengths</h4>
                      <ul className="space-y-1">
                        {activeAiReport.strengths_observed.map((s, i) => (
                          <li key={`mli-${i}`} className="flex items-start gap-2 text-xs text-green-700"><div className="w-1 h-1 bg-green-400 rounded-full mt-1.5 shrink-0" />{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {activeAiReport.areas_of_focus?.length > 0 && (
                    <div className="bg-amber-50 rounded-xl p-4">
                      <h4 className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> Focus Areas</h4>
                      <ul className="space-y-1">
                        {activeAiReport.areas_of_focus.map((a, i) => (
                          <li key={`mli-${i}`} className="flex items-start gap-2 text-xs text-amber-700"><div className="w-1 h-1 bg-amber-400 rounded-full mt-1.5 shrink-0" />{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {activeAiReport.goals_for_next_month?.length > 0 && (
                  <div className="bg-[#ECFEFF]/50 border border-[#22D3EE]/20 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-[#0E7490] mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> Goals</h4>
                    <div className="space-y-1.5">
                      {activeAiReport.goals_for_next_month.map((g, i) => (
                        <div key={`mg-${i}`} className="flex items-start gap-2">
                          <Badge className="rounded-full bg-[#22D3EE] text-white text-[10px] shrink-0 mt-0.5">{i + 1}</Badge>
                          <p className="text-xs text-slate-600">{g}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeAiReport.recommended_adjustments?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-slate-700 mb-2">Recommended Adjustments</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {activeAiReport.recommended_adjustments.map((adj, i) => (
                        <div key={`mr-${i}`} className="p-3 bg-slate-50 rounded-xl">
                          <Badge variant="outline" className="rounded-full text-[10px] capitalize mb-1">{adj.area}</Badge>
                          <p className="text-xs text-slate-600">{adj.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Monthly Report Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-auto">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                  <FileText className="w-5 h-5 text-[#22D3EE]" />
                  {selectedReport.month_label} Report — {selectedReport.child_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-[#ECFEFF] rounded-2xl p-4 text-center">
                    <p className="text-2xl font-extrabold text-[#0E7490]" style={{ fontFamily: 'Nunito' }}>{selectedReport.total_sessions}</p>
                    <p className="text-xs font-semibold text-[#0E7490]/60">Sessions</p>
                  </div>
                  <div className="bg-green-50 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-extrabold text-green-700" style={{ fontFamily: 'Nunito' }}>{selectedReport.completed_sessions}</p>
                    <p className="text-xs font-semibold text-green-600/60">Completed</p>
                  </div>
                  <div className="bg-[#F7FEE7] rounded-2xl p-4 text-center">
                    <p className="text-2xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>{selectedReport.skills_mastered}</p>
                    <p className="text-xs font-semibold text-slate-500">Skills Mastered</p>
                  </div>
                  <div className="bg-purple-50 rounded-2xl p-4 text-center">
                    <p className="text-2xl font-extrabold text-purple-700" style={{ fontFamily: 'Nunito' }}>{selectedReport.avg_intervention_score}</p>
                    <p className="text-xs font-semibold text-purple-600/60">Avg Score</p>
                  </div>
                </div>

                {selectedReport.criteria_progress?.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                      <TrendingUp className="w-4 h-4 text-[#22D3EE]" /> Criteria Progress
                    </h3>
                    <div className="space-y-3">
                      {selectedReport.criteria_progress.map(cp => (
                        <div key={cp.criteria_id} className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-slate-700 text-sm">{cp.title}</span>
                            <Badge variant="outline" className="rounded-full text-xs">
                              Level {cp.latest_level}/{cp.total_levels}
                            </Badge>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2.5">
                            <div
                              className="bg-gradient-to-r from-[#22D3EE] to-[#C4F017] h-2.5 rounded-full transition-all"
                              style={{ width: `${cp.total_levels > 0 ? (cp.latest_level / cp.total_levels) * 100 : 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{cp.ratings_count} rating{cp.ratings_count !== 1 ? 's' : ''} this month</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Object.keys(selectedReport.comfort_summary || {}).length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                      <Smile className="w-4 h-4 text-[#C4F017]" /> Comfort Ratings
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(selectedReport.comfort_summary).map(([key, count]) => {
                        const info = COMFORT_LABELS[key] || { label: key, emoji: '\u2753', color: 'text-slate-600 bg-slate-50' };
                        return (
                          <div key={key} className={`flex items-center gap-2 px-4 py-2 rounded-full ${info.color}`}>
                            <span className="text-lg">{info.emoji}</span>
                            <span className="text-sm font-semibold">{info.label}: {count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
                      <ClipboardList className="w-4 h-4 text-[#22D3EE]" /> Instructor Notes
                    </h3>
                    {isInstructor && !editing && (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-[#22D3EE] gap-1" data-testid="edit-report-notes-btn">
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    )}
                  </div>
                  {editing ? (
                    <div className="space-y-3">
                      <Textarea
                        data-testid="report-notes-input"
                        className="rounded-xl"
                        placeholder="Add notes about this month's progress, goals for next month..."
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <Button onClick={saveNotes} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] font-bold gap-2" data-testid="save-report-notes-btn">
                          <Save className="w-4 h-4" /> Save Notes
                        </Button>
                        <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-4 min-h-[60px]">
                      {selectedReport.instructor_notes ? (
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedReport.instructor_notes}</p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No notes added yet</p>
                      )}
                    </div>
                  )}
                </div>

                {isInstructor && selectedReport.status === 'draft' && (
                  <Button
                    data-testid="publish-report-btn"
                    onClick={publishReport}
                    className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg h-12 gap-2"
                  >
                    <CheckCircle2 className="w-5 h-5" /> Publish Report
                  </Button>
                )}

                <Button
                  data-testid="download-pdf-btn"
                  onClick={downloadPdf}
                  disabled={downloading}
                  variant="outline"
                  className="w-full rounded-full border-2 border-[#22D3EE] text-[#0E7490] hover:bg-[#ECFEFF] font-bold h-12 gap-2"
                >
                  <Download className="w-5 h-5" /> {downloading ? 'Generating PDF...' : 'Download PDF Report'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
